import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BreakdownAccordion } from "../../../../../components/accordions/breakdownAccordion";
import { Button } from "../../../../../components/buttons/button";
import { InformationCard } from "../../../../../components/cards/infoCard";
import { NumberCard } from "../../../../../components/cards/numberCard";
import { TagCard } from "../../../../../components/cards/tagCard";
import { SearchInput } from "../../../../../components/inputs/searchInput";
import { FailedDisbursementsModal } from "../../../../../components/modals/app_modals/loans/failedDisbursementsModal";
import { LoanRepaymentScheduleModal } from "../../../../../components/modals/app_modals/loans/repaymentScheduleModal";
import { UploadLoanRepaymentModal } from "../../../../../components/modals/app_modals/loans/uploadRepaymentModal";
import { TableComponent } from "../../../../../components/table/table";
import { useModalControl } from "../../../../../customHooks/useModalControl";
import {
  formatToCurrency,
  formatToNumber,
  getOffsetFromPage,
  getPageFromOffset,
} from "../../../../../helperFunctions";
import { SingleCooperativeDetails } from "../../../../../redux/features/cooperative/cooperative.type";
import {
  useFetchCooperativeLoanDetailsQuery,
  useFetchFailedDisbursementsQuery,
} from "../../../../../redux/features/loans/loans.api";
import {
  MemberLoansDetailsQuery,
  SingleLoan,
} from "../../../../../redux/features/loans/loans.type";
import { PermissionCode } from "../../../../../redux/features/settings/settings.type";
import { loanStatusMap } from "../../../../../utils/constants";

export const CooperativeOfficialLoansView = ({
  permissions,
  cooperativeDetails,
}: {
  permissions: PermissionCode[];
  cooperativeDetails: SingleCooperativeDetails;
}) => {
  const [activeLoansBreakdown, setActiveLoansBreakdown] = useState<
    { icon: string; title: string; value: number }[]
  >([]);
  const [selectedEntry, setSelectedEntry] = useState<SingleLoan>();
  const [query, setQuery] = useState<MemberLoansDetailsQuery>({
    coop: cooperativeDetails.id,
    logs: true,
    text: undefined,
    limit: 10,
    offset: 0,
  });

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const navigate = useNavigate();

  const {
    data: cooperativeLoans,
    isLoading: loadingCooperativeLoans,
    isFetching: fetchingCooperativeLoans,
    refetch: refetchCooperativeLoans,
  } = useFetchCooperativeLoanDetailsQuery(query, {
    refetchOnMountOrArgChange: true,
  });

  const { data: failedDisbursements, refetch: refetchFailedDisbursements } =
    useFetchFailedDisbursementsQuery(
      {
        coop: cooperativeDetails.id,
        limit: 1,
        offset: 0,
        logs: true,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !permissions.includes("can.approve.loans"),
      }
    );

  useEffect(() => {
    setActiveLoansBreakdown([
      {
        icon: "coins",
        title: "Total Principal",
        value: cooperativeLoans?.data?.total_principal ?? 0,
      },
      {
        icon: "coins",
        title: "Total Principal Outstanding",
        value: cooperativeLoans?.data?.total_principal_outstanding ?? 0,
      },
      {
        icon: "coins",
        title: "Total Interest",
        value: cooperativeLoans?.data?.total_interest ?? 0,
      },
      {
        icon: "coins",
        title: "Total Interest Outstanding",
        value: cooperativeLoans?.data?.total_interest_outstanding ?? 0,
      },

      {
        icon: "coins",
        title: "Total Penalty Fee",
        value: cooperativeLoans?.data?.total_penalty_fees ?? 0,
      },
      {
        icon: "coins",
        title: "Total Penalty Fee Outstanding",
        value: cooperativeLoans?.data?.total_penalty_fees_outstanding ?? 0,
      },
    ]);
  }, [cooperativeLoans]);

  return (
    <>
      <div className="w-full flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <NumberCard
            title={"Total Outstanding Loan Balance"}
            value={cooperativeLoans?.data?.summary}
            loading={loadingCooperativeLoans}
          />

          <NumberCard
            title={"Total Active Loan Count"}
            value={cooperativeLoans?.data?.active_loans_count}
            className="!bg-white !text-pry-green-200 border !min-h-fit !max-w-full"
            iconClassName="!hidden"
            loading={loadingCooperativeLoans}
            isCurrency={false}
          />
        </div>

        <BreakdownAccordion title="See Active Loans Breakdown">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {activeLoansBreakdown?.map((breakdown, idx) => {
              return (
                <NumberCard
                  key={`${idx}`}
                  title={breakdown.title}
                  value={breakdown.value}
                  className="!bg-white !text-pry-green-200 border !min-h-fit !max-w-full"
                  iconClassName="!bg-light-green-bg-300"
                  icon={breakdown.icon}
                  titleClassName="!text-xs"
                  valueClassName="!text-base"
                  loading={loadingCooperativeLoans}
                />
              );
            })}
          </div>
        </BreakdownAccordion>

        {(failedDisbursements?.data?.logs?.count ?? 0) > 0 &&
        permissions.includes("can.approve.loans") ? (
          <InformationCard
            info={
              <span>
                <span className="text-info-purple font-bold">
                  {failedDisbursements?.data?.logs?.count}{" "}
                  {failedDisbursements?.data?.logs?.count === 1
                    ? "loan"
                    : "loans"}
                </span>{" "}
                failed disbursement.
              </span>
            }
            size="mini"
            variant="purple"
            className={"!items-center"}
            actionElement={
              <Button
                text="View all"
                onClick={() => {
                  handleModalOpen("failedDisbursementsModal");
                }}
                type="button"
                className="!text-xs md:!w-fit"
                loading={false}
                disabled={false}
                size="small"
                icon={"refresh"}
                variation="primary-purple"
              />
            }
          />
        ) : undefined}

        <div className="flex items-center flex-wrap gap-2">
          <h3 className="text-base font-medium flex-1">
            Cooperative Loan History
          </h3>

          <SearchInput
            placeholder="Search"
            searchFunction={(val) => {
              setQuery({ ...query, text: val });
            }}
            className="w-full md:max-w-[286px] !px-4 !py-2"
            iconDimension={16}
          />
        </div>

        <TableComponent
          stickyLastCol
          mobileCardView={false}
          className="w-full !min-w-max md:!min-w-full"
          isRowClickable={true}
          headers={["Loan Type", "Loan Amount", "Loan Tenure", "Status"]}
          headerActionProps={{
            hasMoreMenu: true,
          }}
          isLoading={fetchingCooperativeLoans}
          isTableEmpty={[0, undefined].includes(
            cooperativeLoans?.data?.Loan?.results?.length
          )}
          currentPage={getPageFromOffset(query.offset ?? 0, query.limit ?? 0)}
          isPaginated
          itemsPerPage={query?.limit}
          onPageChange={(page) => {
            setQuery({
              ...query,
              offset: getOffsetFromPage(page, query.limit ?? 0),
            });
          }}
          emptrySrc="/assets/images/empty_state_cooperatives.svg"
          emptyTitle="No loans found"
          totalCount={cooperativeLoans?.data?.Loan?.count}
          rows={
            cooperativeLoans?.data?.Loan?.results?.map((item, idx) => {
              return {
                id: item?.id,
                items: [
                  `${item?.LoanProduct?.LoanName ?? "N/A"}`,
                  formatToCurrency(item.Amount, {
                    decimalPlaces: 2,
                    currency: "NGN",
                  }),
                  `${formatToNumber(item.Tenure)} months`,
                  <TagCard
                    text={loanStatusMap[item.Status].text}
                    type={loanStatusMap[item.Status].tagVariant}
                  />,
                ],
                onClick: () => {
                  navigate(
                    `/app/my-cooperatives/${cooperativeDetails.id}/loan/${item.id}/official/view`
                  );
                },
                actionProps: {
                  hasActions: true,
                  hasCheckbox: false,
                  hasMoreMenu: true,
                  moreOptions: [
                    {
                      icon: "calendar",
                      text: "View Schedule",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("viewRepaymentScheduleModal");
                      },
                      hidden: false,
                    },
                    {
                      icon: "arrow-up",
                      text: "Upload Repayment",
                      onClick: () => {
                        setSelectedEntry(item);
                        handleModalOpen("uploadLoanRepaymentModal");
                      },
                      hidden: item.Status !== "active",
                    },
                  ],
                },
              };
            }) ?? []
          }
        />
      </div>

      {selectedEntry ? (
        <>
          <LoanRepaymentScheduleModal
            isModalOpen={modalOpen && modal === "viewRepaymentScheduleModal"}
            onModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            loanDetails={selectedEntry}
            type="official"
          />

          <UploadLoanRepaymentModal
            modal={modal}
            modalOpen={modalOpen}
            handleModalClose={() => {
              handleModalClose();
              setSelectedEntry(undefined);
            }}
            handleModalOpen={handleModalOpen}
            loanDetails={selectedEntry}
            onSuccess={async () => {}}
          />
        </>
      ) : undefined}

      {modalOpen && modal === "failedDisbursementsModal" ? (
        <FailedDisbursementsModal
          isModalOpen={modalOpen && modal === "failedDisbursementsModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          cooperativeDetails={cooperativeDetails}
          onSuccess={async () => {
            refetchCooperativeLoans();
            refetchFailedDisbursements();
            handleModalClose();
          }}
        />
      ) : undefined}
    </>
  );
};
